import { supabase } from '../lib/supabase'
import { likeTerm } from './orders'

/**
 * Stock and ESL list price, from public.rep_stock_price() (migration 0033).
 *
 * The function is the ONLY rep path to the private Brain copies, and its
 * return type is these five columns and nothing else — no tier price
 * (price2..price10), no per-location quantity. It returns rows only for an
 * active member (reps and distributors); not rep-scoped, so nothing here is
 * "my" stock.
 *
 *   list_price  price1 when > 0, else NULL → "Contact ESL for pricing"
 *   available   the website's number: summed over the BC writer's own
 *               location list, NULL when the BC writer would call it
 *               unknown → "Stock unknown", never 0
 *   synced_at   the item's Brain stock sync time ("as of")
 */
export interface ItemStock {
  itemId: string
  itemDescription: string | null
  listPrice: number | null
  available: number | null
  syncedAt: string | null
}

const COLUMNS = 'item_id, item_desc, list_price, available, synced_at'

interface Row {
  item_id: string
  item_desc: string | null
  list_price: number | null
  available: number | null
  synced_at: string | null
}

function toItem(r: Row): ItemStock {
  return {
    itemId: r.item_id,
    itemDescription: r.item_desc,
    listPrice: r.list_price,
    available: r.available,
    syncedAt: r.synced_at,
  }
}

export type ItemStockResult =
  | { status: 'found'; item: ItemStock }
  | { status: 'not_found' }
  | { status: 'error'; detail: string }

/** One item, for the SKU page. not_found = not a PIMBERLY catalog item. */
export async function fetchItemStock(itemId: string): Promise<ItemStockResult> {
  const { data, error } = await supabase
    .rpc('rep_stock_price', undefined, { get: true })
    .select(COLUMNS)
    .eq('item_id', itemId)
    .maybeSingle()

  if (error) return { status: 'error', detail: error.message }
  if (!data) return { status: 'not_found' }
  return { status: 'found', item: toItem(data as Row) }
}

export type ProductSearchResult =
  | { status: 'ok'; items: ItemStock[]; totalCount: number }
  | { status: 'error'; detail: string }

/**
 * Products search: SKU fragment or description word, case-insensitive. An
 * empty term lists every catalog item (about 500), paged. Sorted by SKU.
 */
export async function searchProducts(term: string, limit: number): Promise<ProductSearchResult> {
  let query = supabase
    .rpc('rep_stock_price', undefined, { get: true, count: 'exact' })
    .select(COLUMNS)

  const t = term.trim()
  if (t) {
    const q = likeTerm(t)
    query = query.or(`item_id.ilike."%${q}%",item_desc.ilike."%${q}%"`)
  }

  const { data, error, count } = await query.order('item_id', { ascending: true }).range(0, limit - 1)

  if (error) return { status: 'error', detail: error.message }
  const rows = (data ?? []) as Row[]
  return { status: 'ok', items: rows.map(toItem), totalCount: count ?? rows.length }
}
